import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthApiService } from '../../../core/auth/auth-api.service';
import { ThemeService } from '../../../core/services/theme.service';
import { PhoneInputComponent } from '../../../shared/components/phone-input/phone-input.component';
import { COUNTRIES } from '../../../shared/data/countries';
import { POLICY_VERSION } from '../../../shared/legal/policy-content';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';

@Component({
    selector: 'app-developer-signup',
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterLink, LucideIconsModule, PhoneInputComponent],
    templateUrl: './developer-signup.component.html',
    styleUrls: ['./developer-signup.component.scss'],
})
export class DeveloperSignupComponent {
    private fb = inject(FormBuilder);
    private authApi = inject(AuthApiService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    protected themeService = inject(ThemeService);

    private readonly localLogoBlack = 'https://assetsnibni.zohostratus.com/images/logos/nibni-logo-black.avif';
    private readonly localLogoWhite = 'https://assetsnibni.zohostratus.com/images/logos/nibni-logo-white.avif';

    loading = false;
    message: string | null = null;
    error: string | null = null;
    fieldErrors: Record<string, string> = {};
    showPassword = false;
    showConfirmPassword = false;
    acceptedPolicies = false;

	showEmailModal = false;
	submittedEmail: string | null = null;
	signupSessionId: string | null = null;
	emailSent: boolean | null = null;
	resendLoading = false;
	resendMessage: string | null = null;
    returnUrl: string | null = this.sanitizeReturnUrl(
        this.route.snapshot.queryParamMap.get('returnUrl') || this.route.snapshot.queryParamMap.get('return_url')
    );

	    form = this.fb.group({
	        first_name: ['', Validators.required],
	        last_name: ['', Validators.required],
	        company_name: ['', Validators.required],
	        email_id: ['', [Validators.required, Validators.email]],
	        mobile: this.fb.control({ countryCode: 'EG', phone: '' }),
	        password: ['', Validators.required],
	        confirm_password: ['', Validators.required]
	    });

    get logoSrc(): string {
        const isDarkTheme = this.themeService.theme() !== 'white';
        return isDarkTheme ? this.localLogoWhite : this.localLogoBlack;
    }

    submit(): void {
        this.message = null;
        this.error = null;
        this.fieldErrors = {};
        this.resendMessage = null;

        const orgName = String(this.form.value.company_name || '').trim();
        const firstName = String(this.form.value.first_name || '').trim();
        const lastName = String(this.form.value.last_name || '').trim();
        const fullName = [firstName, lastName].filter(Boolean).join(' ').trim() || null;
        const email = String(this.form.value.email_id || '').trim();
        const phoneValue = this.form.value.mobile;
        const hasPhoneInput = Boolean(String((phoneValue as any)?.phone || '').trim());
        const phone = hasPhoneInput ? this.toE164(phoneValue) : null;
        const password = String(this.form.value.password || '');
        const confirmPassword = String(this.form.value.confirm_password || '');

        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.error = 'Please fill in all required fields.';
            if (!password.trim()) this.fieldErrors['password'] = 'Password is required.';
            if (!confirmPassword.trim()) this.fieldErrors['confirm_password'] = 'Confirm your password.';
            return;
        }

        if (!orgName) {
            this.fieldErrors['company_name'] = 'Company name is required.';
            return;
        }
        if (hasPhoneInput && !phone) {
            this.fieldErrors['mobile'] = 'Enter a valid phone number.';
            return;
        }
        if (!password.trim()) {
            this.fieldErrors['password'] = 'Password is required.';
            return;
        }
        if (!confirmPassword.trim()) {
            this.fieldErrors['confirm_password'] = 'Confirm your password.';
            return;
        }
        if (password !== confirmPassword) {
            this.fieldErrors['confirm_password'] = 'Passwords do not match.';
            return;
        }
        if (password.length < 8 || !/[a-zA-Z]/.test(password) || !/[0-9]/.test(password)) {
            this.fieldErrors['password'] = 'Password must be at least 8 characters and include a letter and a number.';
            return;
        }
        if (!this.acceptedPolicies) {
            this.fieldErrors['policy_acceptance'] = 'You must accept the Terms of Service and Privacy Policy.';
            return;
        }

		this.loading = true;
		this.authApi
				.signUpPartner({
					email,
					phone: phone || null,
					password,
					org_name: orgName,
					first_name: firstName || null,
					last_name: lastName || null,
					full_name: fullName,
                    return_url: this.returnUrl,
                    policy_acceptance: {
                        accepted: true,
                        version: POLICY_VERSION,
                        flow_source: 'signup_partner',
                        policy_keys: ['terms_of_service', 'privacy_policy']
                    }
				})
				.subscribe({
				next: (resp) => {
					this.loading = false;
					const requiresEmailVerification = Boolean(resp?.email_verification_required);
					this.emailSent = typeof resp?.email_sent === 'boolean' ? resp.email_sent : null;
					this.signupSessionId = resp?.signup_session_id ? String(resp.signup_session_id) : null;
					if (this.emailSent === false) {
						this.message = requiresEmailVerification
							? 'Partner account created, but verification email could not be sent.'
							: 'Partner account created, but confirmation email could not be sent.';
                    } else if (this.emailSent === true) {
                        this.message = requiresEmailVerification
                            ? 'Partner account created. Please verify your email.'
                            : 'Partner account created. A confirmation email was sent.';
                    } else {
                        this.message = requiresEmailVerification
                            ? 'Partner account created. Please verify your email.'
                            : 'Partner account created.';
                    }
					this.submittedEmail = email;
					this.showEmailModal = true;
				},
                error: (err) => {
                    this.loading = false;
                    const msg = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to create account.';
                    const lower = String(msg).toLowerCase();
                    if (lower.includes('email')) this.fieldErrors['email_id'] = String(msg);
                    else if (lower.includes('phone')) this.fieldErrors['mobile'] = String(msg);
                    else this.error = String(msg);
                }
            });
	}

	resendEmail(): void {
		if (!this.signupSessionId || this.resendLoading) return;

		this.resendMessage = null;
		this.resendLoading = true;
		this.authApi.resendConfirmEmail({ signup_session_id: this.signupSessionId, return_url: this.returnUrl }).subscribe({
			next: () => {
				this.resendLoading = false;
				this.resendMessage = 'If your account is pending verification, we will send a verification email. Please check your inbox/spam.';
			},
			error: () => {
                this.resendLoading = false;
                this.resendMessage = 'If your account is pending verification, we will send a verification email. Please check your inbox/spam.';
            }
        });
    }

	closeModal(): void {
        const goTo = this.returnUrl;
		this.showEmailModal = false;
		this.resendMessage = null;
		this.emailSent = null;
		this.signupSessionId = null;
        if (goTo) {
            this.router.navigateByUrl(goTo).catch(() => void 0);
        }
	}

    private sanitizeReturnUrl(value: string | null): string | null {
        const raw = String(value || '').trim();
        if (!raw) return null;
        if (!raw.startsWith('/')) return null;
        if (raw.startsWith('//')) return null;
        if (raw.length > 2048) return null;
        return raw;
    }

    private toE164(value: any): string | null {
        const countryCode = String(value?.countryCode || '').toUpperCase().trim();
        const rawPhone = String(value?.phone || '');
        const digits = rawPhone.replace(/[^\d]/g, '').replace(/^0+/, '');

        if (!countryCode || !digits) return null;
        const match = COUNTRIES.find((c) => c.code === countryCode);
        const dial = match?.dial_code || '';
        if (!dial || !dial.startsWith('+')) return null;

        return `${dial}${digits}`;
    }
}
